import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

function ServiceDetail() {
  const { id } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchService = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/services/${id}`);
        setService(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching service:', error);
        setLoading(false);
      }
    };

    fetchService();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Service not found</h2>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-8">
          <div className="w-full h-64 mb-6 bg-blue-50 rounded-lg overflow-hidden">
            <img 
              src={service.image_url || `https://placehold.co/800x400/eef2ff/4f46e5?text=🐾+${encodeURIComponent(service.business_name || 'Pet Service')}`} 
              alt={service.business_name || 'Pet Service'}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.onerror = null; // Prevent infinite loop
                e.target.src = `https://placehold.co/800x400/eef2ff/4f46e5?text=🐾+${encodeURIComponent(service.business_name || 'Pet Service')}`;
              }}
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{service.business_name}</h1>
          {service.business_name && (
            <p className="text-xl text-blue-600 font-medium mb-4">{service.business_name}</p>
          )}
          
          <div className="mb-6">
            <div className="flex items-center mb-2">
              <span className="text-yellow-500 text-2xl">{'★'.repeat(Math.floor(service.rating || 0))}</span>
              <span className="text-gray-300 text-2xl">{'★'.repeat(5 - Math.floor(service.rating || 0))}</span>
              {service.reviews_count > 0 && (
                <span className="text-gray-600 ml-2">({service.reviews_count} reviews)</span>
              )}
            </div>
            {service.isEmergencyService && (
              <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
                24/7 Emergency Service
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
              <h2 className="text-xl font-semibold mb-4">Contact Information</h2>
              <div className="space-y-2">
                {service.address && (
                  <div className="text-gray-600">
                    <span className="font-medium">Address:</span><br />
                    <div className="flex items-start">
                      <div>
                        {service.address.street}<br />
                        {service.address.city}, {service.address.state} {service.address.zipCode}
                      </div>
                      {service.url && (
                        <a 
                          href={service.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-blue-600 hover:text-blue-800"
                          title="Open in Google Maps"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </a>
                      )}
                    </div>
                  </div>
                )}
                {service.contact && (
                  <>
                    {service.contact.phone && (
                      <p className="text-gray-600">
                        <span className="font-medium">Phone:</span> {service.contact.phone}
                      </p>
                    )}
                    {service.contact.email && (
                      <p className="text-gray-600">
                        <span className="font-medium">Email:</span> {service.contact.email}
                      </p>
                    )}
                    {service.contact.website && (
                      <p className="text-gray-600">
                        <span className="font-medium">Website:</span>{' '}
                        <a href={service.contact.website} className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                          Visit Website
                        </a>
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>

            {service.operatingHours && Object.keys(service.operatingHours).length > 0 && (
              <div>
                <h2 className="text-xl font-semibold mb-4">Operating Hours</h2>
                <div className="space-y-2">
                  {Object.entries(service.operatingHours).map(([day, hours]) => (
                    hours && (
                      <div key={day} className="flex justify-between">
                        <span className="capitalize text-gray-600">{day}</span>
                        <span className="text-gray-900">
                          {hours.open} - {hours.close}
                        </span>
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}
          </div>

          {service.petTypes && service.petTypes.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Services for</h2>
              <div className="flex flex-wrap gap-2">
                {service.petTypes.map((pet) => (
                  <span
                    key={pet}
                    className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                  >
                    {pet.charAt(0).toUpperCase() + pet.slice(1).replace('_', ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}

          {service.reviews && service.reviews.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Reviews</h2>
              <div className="space-y-4">
                {service.reviews.map((review, index) => (
                  <div key={index} className="border-b border-gray-200 pb-4">
                    <div className="flex items-center mb-2">
                      <span className="text-yellow-500">{'★'.repeat(Math.floor(review.rating || 0))}</span>
                      <span className="text-gray-300">{'★'.repeat(5 - Math.floor(review.rating || 0))}</span>
                      <span className="text-gray-500 text-sm ml-2">
                        {new Date(review.date).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-gray-600">{review.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ServiceDetail; 